// uiStyles — 전역 UI 일관화 공용 토큰 + 인라인 스타일 헬퍼 (#051).
// 인라인 스타일 코드베이스라 화면마다 색·padding·radius가 제각각이던 것을 통일.
// 실제 사용 빈도가 가장 높은 값을 토큰으로 채택(primary #4F46E5, radius 8 등).
// 적용: import 후 기존 인라인 style을 이 토큰/헬퍼로 점진 교체(데스크톱·모바일 레이아웃 불변).
// (2026-07-05 전역 원복: CLOVA 그린 토큰은 마스터 지시로 폐기 — 그린은 AI노트 스킨(meetingUtil) 전용.
//  selectedText/info는 톤 스윕이 참조 중이라 토큰은 유지하고 값만 인디고 톤에 맞춤.)
// 2026-10-21 시안 F(Notion) 팔레트로 교체(#1867) · CSS 변수·다크 값은 public/index.html
// 2026-10-22 시안 F 전수 수정 f7 — 단추 31/27(상자 모델)·quiet·icon · 입력 13px · 알약 12px·8톤 · 공용 부품은 components/ui

export const COLORS = {
  primary: '#2383e2',        // 주요 강조(버튼·선택)
  primaryDark: '#1873ce',    // 강조 텍스트·hover/active
  primarySoft: '#eaf3fc',    // 선택/연한 배경
  primaryBorder: '#d3e7f9',  // 연한 강조 테두리
  danger: '#d66b60', dangerText: '#a4564d', dangerSoft: '#f8e4e0',
  success: '#539b77', successText: '#426b55', successSoft: '#dcebe2',
  warn: '#8d6d28', warnSoft: '#f8e8b3',
  selectedText: '#32312e',   // 선택 텍스트(시안 F: 파란 글자 아닌 진한 글자 + 회색 바탕)
  info: '#2383e2',           // 정보 강조
  infoSoft: '#eaf3fc',
  text: '#32312e',           // 본문
  textSub: '#64635f',        // 보조
  textMuted: '#92918d',      // 흐림
  border: '#e5e5e2',         // 기본 테두리
  borderSoft: '#eeeeec',     // 옅은 구분선
  inputBorder: '#e5e5e2',
  bg: '#ffffff',             // 페이지 배경
  surface: '#ffffff',        // 카드/표면
};

export const RADIUS = { xs: '3px', sm: '4px', md: '5px', lg: '8px', xl: '10px', chip: '20px', pill: '999px' };
export const SPACE = { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' };
export const FONT = { xs: '11px', sm: '12px', base: '13px', md: '14px', lg: '15px', xl: '17px' };

// 그림자 — btn = 단추 옅은 그림자(시안 F .btn) · float = 대화상자·떠 있는 판
export const SHADOW = {
  btn: '0 1px 1px #eeeeec',
  float: 'var(--gw-float-shadow, 0 4px 24px #1c191711, 0 1px 3px #1c19170b)',
};

// 아이콘 선 굵기(시안 F svg.i) — tabler 아이콘에 stroke={ICON_STROKE}
export const ICON_STROKE = 1.65;

// 8톤 — soft = 알약·태그 바탕 · ink = 글자·점(시안 F 토큰 hex · 다크는 별도 잡)
export const TONES = {
  blue:   { soft: '#d3e7f9', ink: '#356c9b' },
  purple: { soft: '#eee5f5', ink: '#7c5e96' },
  yellow: { soft: '#f8e8b3', ink: '#8d6d28' },
  green:  { soft: '#dcebe2', ink: '#426b55' },
  red:    { soft: '#f8e4e0', ink: '#a4564d' },
  orange: { soft: '#f9eadc', ink: '#d28b50' },
  pink:   { soft: '#f3e1eb', ink: '#bb799e' },
  gray:   { soft: '#f1f1ef', ink: '#64635f' },
};

// 버튼 — variant: 'primary' | 'secondary' | 'accent' | 'danger' | 'quiet' | 'icon', size: 'sm' | 'md'
// 높이는 상자 모델로 맞춘다(줄높이 15 + 위아래 패딩 + 1px 테두리 2 = md 31 · sm 27). display·정렬은 건드리지 않는다.
export function btn(variant = 'primary', size = 'md') {
  const sz = size === 'sm'
    ? { padding: '5px 9px', fontSize: FONT.sm }
    : { padding: '7px 11px', fontSize: FONT.base };
  const base = { ...sz, borderRadius: RADIUS.md, border: '1px solid transparent', cursor: 'pointer', fontWeight: 500, lineHeight: '15px', whiteSpace: 'nowrap' };
  const v = {
    primary:   { background: COLORS.primary, color: '#fff', boxShadow: SHADOW.btn },
    secondary: { background: COLORS.surface, color: COLORS.text, border: `1px solid ${COLORS.border}`, boxShadow: SHADOW.btn },
    accent:    { background: COLORS.surface, color: COLORS.primaryDark, border: `1px solid ${COLORS.primaryBorder}`, boxShadow: SHADOW.btn },
    danger:    { background: COLORS.dangerSoft, color: COLORS.dangerText },
    quiet:     { background: 'transparent', color: COLORS.textSub, padding: '6px 8px', border: 'none', fontWeight: 400 },
    icon:      { background: 'transparent', color: COLORS.textMuted, width: '28px', height: '28px', padding: 0, border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  }[variant] || {};
  return { ...base, ...v };
}

// 입력 — background·color 는 넣지 않는다(disabled 입력의 브라우저 회색 표시를 지우지 않게) · outline 도 넣지 않는다(`public/index.html:62` focus-visible 링을 가리지 않게)
export const input = {
  width: '100%', boxSizing: 'border-box', padding: '7px 10px',
  border: `1px solid ${COLORS.inputBorder}`, borderRadius: RADIUS.md, fontSize: FONT.base,
};

// 카드(표면)
export const card = {
  background: COLORS.surface, border: `1px solid ${COLORS.borderSoft}`,
  borderRadius: RADIUS.lg, boxShadow: 'none', padding: '16px 18px',
};

// 상태 배지 — tone: 'default' | 'success' | 'warn' | 'danger'(기존 색 그대로) | TONES 8톤(blue·purple·yellow·green·red·orange·pink·gray)
// 점은 inline 스타일로 못 그린다 — 점이 필요하면 components/ui 의 Pill.
export function badge(tone = 'default') {
  const legacy = {
    default: { background: COLORS.primarySoft, color: COLORS.primaryDark },
    success: { background: COLORS.successSoft, color: COLORS.successText },
    warn:    { background: COLORS.warnSoft, color: COLORS.warn },
    danger:  { background: COLORS.dangerSoft, color: COLORS.dangerText },
  };
  const has = (obj) => Object.prototype.hasOwnProperty.call(obj, tone);
  const t = has(legacy)
    ? legacy[tone]
    : has(TONES) ? { background: TONES[tone].soft, color: TONES[tone].ink } : {};
  return {
    display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 8px', borderRadius: RADIUS.chip,
    fontSize: FONT.sm, lineHeight: '18px', fontWeight: 500, whiteSpace: 'nowrap', ...t,
  };
}
